// Pure derivations for the carriers pages (Carriers.js, CarrierDetail.js).
// Per-carrier behaviour comes from CONFIGURATION, never from a name test in a
// component: the carrier descriptor registry (GET /api/logistics/carrier-
// descriptors — catalog, auth.otp, auth.scope_dimensions, claim_identity,
// limits) said for the tenant carrier it matches, and its activity from
// GET /api/statistics/carriers (tickets per canonical carrier, its services).

// Accent- and case-insensitive key (the backend matches casefolded aliases;
// its alias lists carry both « colis privé » and « colis prive »).
export const fold = (value) => String(value || '')
  .normalize('NFD').replace(/[̀-ͯ]/g, '')
  .toLowerCase().trim();

// The descriptor of a tenant carrier: same canonical name first, else the
// longest alias found in the name (« Mondial Relay C2D » → Mondial Relay).
export const descriptorFor = (name, descriptors) => {
  const key = fold(name);
  if (!key || !Array.isArray(descriptors)) return null;
  const exact = descriptors.find((d) => fold(d?.name) === key);
  if (exact) return exact;
  let best = null;
  let bestLength = 0;
  descriptors.forEach((descriptor) => {
    (descriptor?.identity?.aliases || []).forEach((alias) => {
      const folded = fold(alias);
      if (folded && key.includes(folded) && folded.length > bestLength) {
        best = descriptor;
        bestLength = folded.length;
      }
    });
  });
  return best;
};

// An e-mail entry is an address or a rule object { address, tracking_patterns }.
export const emailAddress = (entry) => (entry && typeof entry === 'object' ? String(entry.address || '') : String(entry || ''));
export const emailPatterns = (entry) => (entry && typeof entry === 'object' && Array.isArray(entry.tracking_patterns) ? entry.tracking_patterns : []);
export const carrierAddresses = (carrier) => (carrier?.emails || []).map(emailAddress).filter(Boolean);

// How the agent reaches the carrier: the tenant's own switch (is_mail) first,
// then the catalog detail of its descriptor.
export const contactMode = (carrier, descriptor) => {
  const detail = descriptor?.catalog?.detail;
  if (carrier?.is_mail) return detail === 'mail_create_portal_updates' ? 'email_portal' : 'email';
  if (detail === 'form_claims') return 'form';
  if (detail === 'api_claims') return 'api';
  return 'portal';
};

// A portal account is needed when the agent works on the portal and the
// catalog says its portal asks for one (an unknown non-mail carrier: maybe).
export const needsPortalAccount = (carrier, descriptor) => {
  if (!carrier || carrier.is_mail) return false;
  if (!descriptor) return null;
  return Boolean(descriptor.catalog?.requires_credentials);
};

// Whether the carrier's descriptor uses a portal login anywhere — the
// « Accès au portail » tab (accounts + login-code mailbox) is shown only then
// (Kevin 29/10). Read from the descriptor, never from the carrier's name: a
// portal channel, any channel that needs credentials (proofs read on the
// portal), a form posted with the portal account (validation_dispatch.
// credentials_carrier), the catalog's requires_credentials, a login-code
// mailbox.
export const offersPortalAccess = (descriptor) => {
  if (!descriptor) return false;
  const channels = Array.isArray(descriptor.channels) ? descriptor.channels : [];
  return channels.some((channel) => channel?.type === 'portal' || channel?.requires_credentials === true)
    || Boolean(descriptor.catalog?.requires_credentials)
    || Boolean(descriptor.validation_dispatch?.credentials_carrier)
    || Boolean(descriptor.auth?.otp?.mailbox_required);
};

export const needsOtpMailbox = (carrier, descriptor) => Boolean(
  carrier && !carrier.is_mail && descriptor?.auth?.otp?.mailbox_required,
);

export const usesEnseignes = (carrier, descriptor) => Boolean(
  (descriptor?.auth?.scope_dimensions || []).includes('enseigne')
  || (carrier?.enseignes || []).length > 0
  || carrier?.default_enseigne,
);

export const usesClaimIdentity = (carrier, descriptor) => Boolean(
  descriptor?.claim_identity || carrier?.claim_contact || carrier?.claim_bank?.configured,
);

export const messageLimit = (descriptor) => {
  const value = Number(descriptor?.limits?.message_max_chars);
  return value > 0 ? value : null;
};

// Pages of the app that belong to one carrier's own product (kept in ONE
// table, keyed by canonical name, instead of a name test in a component).
// Colissimo: the return-label contract that feeds the ticket's « étiquette
// retour » action (moved out of Paramètres, Kevin 2026-10-06).
export const CARRIER_SETTINGS_PAGES = {
  Colissimo: { path: '/colissimo-params', labelKey: 'carriersUi.detail.colissimoReturns', label: 'Étiquettes retour' },
};
export const settingsPageFor = (descriptor) => (descriptor ? CARRIER_SETTINGS_PAGES[descriptor.name] || null : null);

// The stats row of a carrier (tickets of the window, its services).
export const activityFor = (carrier, descriptor, statsRows) => {
  if (!Array.isArray(statsRows)) return null;
  const keys = new Set([fold(carrier?.name), fold(descriptor?.name)].filter(Boolean));
  return statsRows.find((row) => keys.has(fold(row?.carrier))) || null;
};

// A carrier must carry at least this many tickets of the 30-day window to be
// listed as « not configured » (Kevin 29/10: the long tail is noise).
export const UNCONFIGURED_MIN_TICKETS = 3;

// Carriers present on the window's tickets that the account has not set up
// (the agent cannot contact them), minus those already requested and those
// under UNCONFIGURED_MIN_TICKETS tickets.
export const unconfiguredCarriers = (statsRows, carriers, descriptors, pendingRequestNames = [], minTickets = UNCONFIGURED_MIN_TICKETS) => {
  if (!Array.isArray(statsRows)) return [];
  const known = new Set();
  (carriers || []).forEach((carrier) => {
    known.add(fold(carrier.name));
    const descriptor = descriptorFor(carrier.name, descriptors);
    if (descriptor) known.add(fold(descriptor.name));
  });
  const requested = new Set(pendingRequestNames.map(fold));
  return statsRows
    .filter((row) => row?.carrier && Number(row.count) >= Math.max(1, minTickets)
      && !known.has(fold(row.carrier)) && !requested.has(fold(row.carrier)))
    .map((row) => ({ name: row.carrier, count: row.count }));
};

// Everything the list needs per carrier, from what the API returned.
// credentials: { [carrierId]: [...] } (undefined = not loaded for it);
// otp: { [carrierId]: { configured } }.
export const carrierFacts = (carrier, { descriptors, credentials, otp, statsRows } = {}) => {
  const descriptor = descriptorFor(carrier.name, descriptors);
  const accounts = credentials ? credentials[carrier.id] : undefined;
  const needsAccount = needsPortalAccount(carrier, descriptor);
  const needsOtp = needsOtpMailbox(carrier, descriptor);
  const addresses = carrierAddresses(carrier);
  const defaults = descriptor?.identity?.default_addresses || [];
  return {
    descriptor,
    mode: contactMode(carrier, descriptor),
    addresses,
    accounts,
    accountsLoaded: Array.isArray(accounts),
    needsAccount,
    missingAccount: needsAccount === true && Array.isArray(accounts) && accounts.length === 0,
    invalidAccounts: Array.isArray(accounts) ? accounts.filter((account) => account.is_valid === false) : [],
    needsOtp,
    otp: otp ? otp[carrier.id] : undefined,
    missingOtp: needsOtp && Boolean(otp?.[carrier.id]) && !otp[carrier.id].configured,
    missingAddress: Boolean(carrier.is_mail) && addresses.length === 0 && defaults.length === 0,
    missingRules: !String(carrier.contact_rules || '').trim(),
    activity: activityFor(carrier, descriptor, statsRows),
  };
};

// The list filters kept in the URL (?flag=…). missing_rules and
// missing_credentials are the historical values other links may carry.
export const FLAGS = ['missing_credentials', 'missing_otp', 'missing_rules'];
export const matchesFlag = (facts, flag) => {
  if (flag === 'missing_credentials') return facts.missingAccount || facts.invalidAccounts.length > 0;
  if (flag === 'missing_otp') return facts.missingOtp;
  if (flag === 'missing_rules') return facts.missingRules;
  return true;
};

// « À vérifier » of the list: one row per thing a person must do, blocking
// first. Each row is a field the API returned — never a guess.
export const carriersAttention = ({ carriers, factsById, mailSender, unconfigured }) => {
  const items = [];
  if (mailSender?.source === 'account_mailbox' && mailSender.problem) {
    items.push({ key: 'mail-sender', tone: 'danger', kind: 'mailSender', detail: mailSender.problem });
  }
  (carriers || []).forEach((carrier) => {
    const facts = factsById[carrier.id];
    if (!facts) return;
    facts.invalidAccounts.forEach((account) => items.push({
      key: `invalid-${account.id}`, tone: 'danger', kind: 'invalidAccount', carrier, account,
    }));
  });
  (carriers || []).forEach((carrier) => {
    const facts = factsById[carrier.id];
    if (!facts) return;
    if (facts.missingAccount) items.push({ key: `account-${carrier.id}`, tone: 'warning', kind: 'missingAccount', carrier });
    if (facts.missingOtp) items.push({ key: `otp-${carrier.id}`, tone: 'warning', kind: 'missingOtp', carrier });
    if (facts.missingAddress) items.push({ key: `address-${carrier.id}`, tone: 'warning', kind: 'missingAddress', carrier });
  });
  if (unconfigured && unconfigured.length > 0) {
    items.push({ key: 'unconfigured', tone: 'warning', kind: 'unconfigured', carriers: unconfigured });
  }
  return items;
};

// « À vérifier » of one carrier (detail page).
export const carrierAttention = ({ carrier, descriptor, credentials, otpMailbox }) => {
  if (!carrier) return [];
  const items = [];
  (credentials || []).filter((account) => account.is_valid === false).forEach((account) => items.push({
    key: `invalid-${account.id}`, tone: 'danger', kind: 'invalidAccount', account, tab: 'portal',
  }));
  if (needsPortalAccount(carrier, descriptor) === true && Array.isArray(credentials) && credentials.length === 0) {
    items.push({ key: 'account', tone: 'warning', kind: 'missingAccount', tab: 'portal' });
  }
  if (needsOtpMailbox(carrier, descriptor) && otpMailbox && !otpMailbox.configured) {
    items.push({ key: 'otp', tone: 'warning', kind: 'missingOtp', tab: 'portal' });
  }
  const defaults = descriptor?.identity?.default_addresses || [];
  if (carrier.is_mail && carrierAddresses(carrier).length === 0 && defaults.length === 0) {
    items.push({ key: 'address', tone: 'warning', kind: 'missingAddress', tab: 'contact' });
  }
  if (descriptor?.claim_identity && !carrier.claim_contact) {
    items.push({ key: 'claim', tone: 'warning', kind: 'missingClaim', tab: 'claim' });
  }
  return items;
};

// « 2026-09-28 » 30 days before now, for /api/statistics/carriers.
export const windowStart = (days = 30, now = new Date()) => {
  const start = new Date(now.getTime() - days * 24 * 3600 * 1000);
  return start.toISOString().slice(0, 10);
};

export const formatCount = (value, lang = 'fr') => Number(value || 0).toLocaleString(lang === 'fr' ? 'fr-FR' : lang);
export const formatShare = (value, lang = 'fr') => `${Math.round(Number(value) || 0).toLocaleString(lang === 'fr' ? 'fr-FR' : lang)} %`;
